import { useInput } from 'react-admin';
import { Stack, Box, Typography, Button, IconButton, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { tokens } from '../theme.js';

/**
 * Inline editor for a JSON array of `{ value, label }` highlight rows.
 * Used by the Facility form to manage the three big-number callouts that
 * appear under each facility's description on the public page.
 */
export function SpecsEditor({ source = 'specs', label = 'Highlights' }) {
  const { field } = useInput({ source });
  const rows = Array.isArray(field.value) ? field.value : [];

  const update = (next) => field.onChange(next);

  const setRow = (i, patch) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    update(next);
  };
  const removeRow = (i) => update(rows.filter((_, idx) => idx !== i));
  const addRow = () => update([...rows, { value: '', label: '' }]);
  const moveRow = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= rows.length) return;
    const next = rows.slice();
    [next[i], next[j]] = [next[j], next[i]];
    update(next);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ color: tokens.ink3, fontSize: 12, lineHeight: 1.6, display: 'block', mb: 1.5 }}>
        Big-number callouts shown under the facility description (typically 3 rows). Each row has a short value (e.g. <code>15,000+</code>) and a label (e.g. <code>Books</code>).
      </Typography>

      <Stack spacing={1}>
        {rows.map((row, i) => (
          <Box
            key={i}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '160px 1fr auto' },
              gap: 1.5,
              alignItems: 'center',
              p: 1.5,
              border: `1px solid ${tokens.line}`,
              borderRadius: 1,
              background: tokens.paper,
            }}
          >
            <TextField
              size="small"
              label="Value"
              value={row.value || ''}
              onChange={(e) => setRow(i, { value: e.target.value })}
              placeholder="15,000+"
            />
            <TextField
              size="small"
              label="Label"
              value={row.label || ''}
              onChange={(e) => setRow(i, { label: e.target.value })}
              placeholder="Books"
            />
            <Stack direction="row" spacing={0.5}>
              <IconButton size="small" onClick={() => moveRow(i, -1)} disabled={i === 0} aria-label="Move up">
                <ArrowUpIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => moveRow(i, 1)} disabled={i === rows.length - 1} aria-label="Move down">
                <ArrowDownIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => removeRow(i)} aria-label="Remove">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        ))}
      </Stack>

      <Button
        size="small"
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={addRow}
        sx={{ mt: 1.5 }}
      >
        Add highlight
      </Button>
    </Box>
  );
}
